import { useCallback, useEffect, useMemo, useState } from "react";
import { SvgTemplate } from "../types/templates";

type RemoteSource = GithubRemoteSource | AwsArchitectureSource;

interface RemoteSourceBase {
  id: string;
  category: string;
  label: string;
  description?: string;
}

interface GithubRemoteSource extends RemoteSourceBase {
  type: "github";
  owner: string;
  repo: string;
  path: string;
}

interface AwsArchitectureSource extends RemoteSourceBase {
  type: "aws-architecture";
  query?: Record<string, string>;
  size?: number;
}

interface UseRemoteSvgTemplatesResult {
  templates: SvgTemplate[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  fetchedAt: Date | null;
}

const USER_AGENT = "animate-svg-react-app";
const GITHUB_API_ROOT = "https://api.github.com/repos";
const AWS_ARCHITECTURE_API = "https://aws.amazon.com/api/dirs/items/search";

const REMOTE_SOURCES: RemoteSource[] = [
  {
    type: "github",
    id: "aws-compute-icons",
    owner: "aws-samples",
    repo: "aws-architecture-icons",
    path: "Architecture-Service-Icons/SVG/Compute",
    category: "AWS",
    label: "AWS Architecture Icons — Compute",
    description: "Latest AWS Compute service glyphs straight from the official icon pack.",
  },
  {
    type: "github",
    id: "aws-storage-icons",
    owner: "aws-samples",
    repo: "aws-architecture-icons",
    path: "Architecture-Service-Icons/SVG/Storage",
    category: "AWS",
    label: "AWS Architecture Icons — Storage",
    description: "Storage service icons to round out your infrastructure diagrams.",
  },
  {
    type: "github",
    id: "azure-icons",
    owner: "Azure",
    repo: "azure-quickstart-templates",
    path: "quickstarts/microsoft.network/application-gateway-waf-webapp",
    category: "Azure",
    label: "Azure sample architecture",
    description: "Reference assets from Azure quickstart samples.",
  },
  {
    type: "aws-architecture",
    id: "aws-reference-architecture",
    category: "AWS",
    label: "AWS Reference Architecture Diagrams",
    description: "Curated AWS reference architectures with downloadable SVG diagrams.",
    query: {
      "item.directoryId": "architecture-center",
      "item.locale": "en_US",
      sort_by: "item.additionalFields.sortDate",
      sort_order: "desc",
    },
    size: 200,
  },
];

const buildGithubUrl = (source: GithubRemoteSource) =>
  `${GITHUB_API_ROOT}/${source.owner}/${source.repo}/contents/${source.path}`;

const ensureAbsoluteUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed.toLowerCase().endsWith(".svg")) {
    return null;
  }

  const normalized = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;

  try {
    const url = new URL(normalized, "https://aws.amazon.com");
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
  } catch (error) {
    // Ignore malformed URLs and continue scanning other values.
  }

  return null;
};

const collectSvgUrls = (value: unknown, accumulator: Set<string>) => {
  if (!value) {
    return;
  }

  if (typeof value === "string") {
    const url = ensureAbsoluteUrl(value);
    if (url) {
      accumulator.add(url);
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectSvgUrls(entry, accumulator));
    return;
  }

  if (typeof value === "object") {
    Object.values(value as Record<string, unknown>).forEach((entry) => collectSvgUrls(entry, accumulator));
  }
};

const extractFieldTags = (fields: Record<string, unknown> | undefined, keys: string[]): string[] => {
  if (!fields) {
    return [];
  }

  const tags = new Set<string>();

  keys.forEach((key) => {
    const raw = fields[key];
    if (!raw) {
      return;
    }

    if (typeof raw === "string") {
      raw
        .split(/[;,|]/)
        .map((token) => token.trim())
        .filter(Boolean)
        .forEach((token) => tags.add(token));
      return;
    }

    if (Array.isArray(raw)) {
      raw
        .map((token) => (typeof token === "string" ? token.trim() : ""))
        .filter(Boolean)
        .forEach((token) => tags.add(token));
    }
  });

  return Array.from(tags);
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const fetchGithubTemplates = async (
  source: GithubRemoteSource,
  headers: HeadersInit
): Promise<SvgTemplate[]> => {
  const response = await fetch(buildGithubUrl(source), { headers });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  const payload = (await response.json()) as Array<{
    name: string;
    download_url?: string;
    html_url?: string;
    path: string;
    type: string;
  }>;

  const svgFiles = payload.filter((item) => item.type === "file" && item.name.toLowerCase().endsWith(".svg"));

  return svgFiles.map<SvgTemplate>((item) => ({
    id: `${source.id}-${item.path}`,
    name: item.name.replace(/\.svg$/i, ""),
    category: source.category,
    path: item.download_url ?? item.html_url ?? "",
    metadata: {
      description: source.description ?? `${source.label} asset`,
      provider: source.label,
      tags: [source.category, "GitHub", source.label],
    },
    source: "github",
    repository: `${source.owner}/${source.repo}`,
  }));
};

interface AwsDirectorySearchItem {
  item?: {
    additionalFields?: Record<string, unknown>;
    assets?: Array<{ asset?: { url?: string | null; name?: string | null; type?: string | null } | null }>;
    body?: string;
    description?: string;
    headline?: string;
    id?: string;
    permalink?: string;
    shortDescription?: string;
    summary?: string;
    title?: string;
  };
}

interface AwsDirectorySearchResponse {
  items?: AwsDirectorySearchItem[];
}

const fetchAwsArchitectureTemplates = async (
  source: AwsArchitectureSource,
  headers: HeadersInit
): Promise<SvgTemplate[]> => {
  const url = new URL(AWS_ARCHITECTURE_API);
  const params: Record<string, string> = {
    "item.directoryId": "architecture-center",
    "item.locale": "en_US",
    sort_by: "item.additionalFields.sortDate",
    sort_order: "desc",
    ...source.query,
  };

  if (source.size) {
    params.size = String(source.size);
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });

  const response = await fetch(url.toString(), {
    headers: {
      ...headers,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }

  const payload = (await response.json()) as AwsDirectorySearchResponse;
  const items = payload.items ?? [];
  const templates: SvgTemplate[] = [];

  items.forEach((entry, entryIndex) => {
    const item = entry.item ?? {};
    const additionalFields = item.additionalFields ?? {};
    const svgCandidates = new Set<string>();

    collectSvgUrls(additionalFields, svgCandidates);

    if (item.assets) {
      item.assets.forEach((assetWrapper) => {
        const asset = assetWrapper?.asset;
        if (!asset?.url) {
          return;
        }
        const normalized = ensureAbsoluteUrl(asset.url);
        if (normalized) {
          svgCandidates.add(normalized);
        }
      });
    }

    if (svgCandidates.size === 0) {
      return;
    }

    const title = item.title ?? item.headline ?? `AWS Reference Architecture ${entryIndex + 1}`;
    const baseDescription =
      item.summary ?? item.shortDescription ?? item.description ?? source.description ?? "AWS reference architecture";
    const link = item.permalink ?? undefined;
    const additionalTags = extractFieldTags(additionalFields, [
      "serviceCategory",
      "services",
      "servicesUsed",
      "topic",
      "industry",
      "architectureType",
      "useCase",
      "category",
    ]);

    const tagSet = new Set<string>([source.category, source.label, "AWS Architecture", ...additionalTags]);
    const tagList = Array.from(tagSet);

    const baseSlug = item.id ? slugify(String(item.id)) : slugify(title);

    Array.from(svgCandidates).forEach((svgUrl, svgIndex) => {
      const suffix = svgCandidates.size > 1 ? ` (${svgIndex + 1})` : "";
      const slug = `${baseSlug}-${svgIndex}`;
      templates.push({
        id: `${source.id}-${slug}`,
        name: `${title}${suffix}`,
        category: source.category,
        path: svgUrl,
        metadata: {
          description: baseDescription,
          provider: source.label,
          link,
          tags: tagList,
        },
        source: "aws",
      });
    });
  });

  return templates;
};

export const useRemoteSvgTemplates = (): UseRemoteSvgTemplatesResult => {
  const [templates, setTemplates] = useState<SvgTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(0);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const refresh = useCallback(() => {
    setRequestId((id) => id + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchTemplates = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const githubHeaders: HeadersInit = {
          Accept: "application/vnd.github+json",
          "User-Agent": USER_AGENT,
        };

        const baseHeaders: HeadersInit = {
          "User-Agent": USER_AGENT,
        };

        const responses = await Promise.all(
          REMOTE_SOURCES.map(async (source) => {
            try {
              if (source.type === "github") {
                return await fetchGithubTemplates(source, githubHeaders);
              }

              if (source.type === "aws-architecture") {
                return await fetchAwsArchitectureTemplates(source, baseHeaders);
              }

              return [];
            } catch (innerError: unknown) {
              if (innerError instanceof Error) {
                throw new Error(`${source.label}: ${innerError.message}`);
              }

              throw new Error(`${source.label}: Unable to fetch remote templates.`);
            }
          })
        );

        if (!isMounted) {
          return;
        }

        const flatTemplates = responses.flat().filter((template) => template.path);
        setTemplates(flatTemplates);
        setFetchedAt(new Date());
      } catch (fetchError: unknown) {
        if (!isMounted) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : "Unable to load remote templates from remote sources.";
        setError(message);
        setTemplates([]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      isMounted = false;
    };
  }, [requestId]);

  const dedupedTemplates = useMemo(() => {
    const seen = new Set<string>();
    return templates.filter((template) => {
      if (!template.path || seen.has(template.path)) {
        return false;
      }
      seen.add(template.path);
      return true;
    });
  }, [templates]);

  return {
    templates: dedupedTemplates,
    isLoading,
    error,
    refresh,
    fetchedAt,
  };
};

export default useRemoteSvgTemplates;
